import { apiRequest } from './axiosConfig';

// Types for extracted images from URL
export interface ExtractedImage {
  url: string;
  alt: string;
  type: 'img' | 'og' | 'twitter' | 'picture';
  width: number | null;
  height: number | null;
  suggested_category: 'brand_logo' | 'product_image' | 'material';
}

export interface ExtractImagesResponse {
  data: ExtractedImage[];
  message: string;
}

// Types for product images
export interface ProductImage {
  id: number;
  product_id: number;
  category: 'brand_logo' | 'product_image' | 'material';
  title: string | null;
  description: string | null;
  file_path: string;
  file_name: string;
  mime_type: string;
  file_size: number;
  sort_order: number;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
  signed_url?: string;
  // Backend appends this presigned/CDN URL on every product image response (ProductImage::getUrlAttribute).
  url?: string;
  // Only populated for category === 'brand_logo'. null = tone not yet classified.
  logo_tone?: 'light' | 'dark' | 'color' | null;
}

export interface BatchUploadRequest {
  images: {
    url: string;
    category: 'brand_logo' | 'product_image' | 'material';
    title?: string;
    description?: string;
  }[];
}

export interface BatchUploadResponse {
  data: ProductImage[];
  errors: {
    index: number;
    url: string;
    error: string;
  }[];
  message: string;
}

export interface ProductImagesResponse {
  data: ProductImage[];
}

export type ProductImageImportInput = BatchUploadRequest['images'][number];

export interface ProductImageImportItem extends ProductImageImportInput {
  id: number;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  error: string | null;
  image: ProductImage | null;
}

export interface ProductImageImport {
  id: string;
  product_id: number;
  total: number;
  completed: number;
  failed: number;
  pending: number;
  items: ProductImageImportItem[];
}

class ProductImageService {
  startImport(productId: number, requestId: string, images: ProductImageImportInput[], signal?: AbortSignal): Promise<{ data: ProductImageImport }> {
    return apiRequest.post(`knowledge/products/${productId}/images/imports`, {
      request_id: requestId,
      images,
    }, { timeout: 30000, signal });
  }

  getImport(productId: number, importId: string, signal?: AbortSignal): Promise<{ data: ProductImageImport }> {
    return apiRequest.get(`knowledge/products/${productId}/images/imports/${importId}`, { timeout: 30000, signal });
  }

  retryImport(productId: number, importId: string, itemIds: number[], signal?: AbortSignal): Promise<{ data: ProductImageImport }> {
    return apiRequest.post(`knowledge/products/${productId}/images/imports/${importId}/retry`, {
      item_ids: itemIds,
    }, { timeout: 30000, signal });
  }

  /**
   * Extract images from a URL
   * @param url URL to extract images from
   * @returns List of extracted images
   */
  async extractImagesFromUrl(url: string, signal?: AbortSignal): Promise<ExtractImagesResponse> {
    try {
      console.log('URLから画像を抽出中...', url);

      const config = {
        // バックエンドは 3 段階でフォールバックする:
        //   1. 通常の HTTP 取得（大半はここで終わる / 数秒）
        //   2. クローラー向けプリレンダリングを狙った再取得（+ 数秒）
        //   3. ヘッドレスブラウザでのレンダリング（CSR サイト用 / 最大 45 秒）
        // 3 段目まで落ちたときに 30 秒で切ると、取得できるはずの画像を捨ててしまう。
        timeout: 75000,
        signal,
      };

      const response = await apiRequest.post<ExtractImagesResponse>(
        'knowledge/products/extract-images-from-url',
        { url },
        config
      );

      console.log(`${response.data.length}件の画像を抽出しました`);
      return response;
    } catch (error: any) {
      console.error('画像抽出に失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Batch upload images from URLs for a product
   * @param productId Product ID
   * @param images Array of image data to upload
   * @returns Uploaded images and any errors
   */
  async batchUploadFromUrl(productId: number, images: BatchUploadRequest['images']): Promise<BatchUploadResponse> {
    try {
      console.log(`Product(ID: ${productId})に${images.length}件の画像をアップロード中...`);

      const config = {
        timeout: 120000, // 2 minutes for batch upload
      };

      const response = await apiRequest.post<BatchUploadResponse>(
        `knowledge/products/${productId}/images/batch-url`,
        { images },
        config
      );

      console.log(`${response.data.length}件の画像をアップロードしました`);
      if (response.errors.length > 0) {
        console.warn(`${response.errors.length}件の画像でエラーが発生しました`);
      }

      return response;
    } catch (error: any) {
      // Handle 422 response which may contain partial success data
      // Backend returns 422 when ALL images fail, but still includes data/errors structure
      if (error?.response?.status === 422 && error?.response?.data) {
        const responseData = error.response.data as BatchUploadResponse;
        console.warn('全ての画像の保存に失敗しました:', responseData.errors?.length || 0, '件のエラー');
        // Return the response structure so caller can display individual errors
        return {
          data: responseData.data || [],
          errors: responseData.errors || [],
          message: responseData.message || '画像の保存に失敗しました',
        };
      }
      console.error('画像のバッチアップロードに失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Get all images for a product
   * @param productId Product ID
   * @param category Optional category filter
   * @returns List of product images
   */
  async getProductImages(productId: number, category?: string): Promise<ProductImagesResponse> {
    try {
      console.log(`Product(ID: ${productId})の画像を取得中...`);

      const config = {
        timeout: 30000,
        params: category ? { category } : undefined,
      };

      const response = await apiRequest.get<ProductImagesResponse>(
        `knowledge/products/${productId}/images`,
        config
      );

      console.log(`${response.data.length}件の画像を取得しました`);
      return response;
    } catch (error: any) {
      console.error('画像取得に失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Upload single image from URL for a product
   * @param productId Product ID
   * @param url Image URL
   * @param category Image category
   * @param title Optional title
   * @param description Optional description
   * @returns Uploaded image
   */
  async uploadFromUrl(
    productId: number,
    url: string,
    category: 'brand_logo' | 'product_image' | 'material',
    title?: string,
    description?: string
  ): Promise<{ data: ProductImage; message: string }> {
    try {
      console.log(`Product(ID: ${productId})にURLから画像をアップロード中...`);

      const config = {
        timeout: 60000,
      };

      const response = await apiRequest.post<{ data: ProductImage; message: string }>(
        `knowledge/products/${productId}/images/url`,
        { url, category, title, description },
        config
      );

      console.log('画像のアップロードが完了しました');
      return response;
    } catch (error: any) {
      console.error('画像アップロードに失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Upload a single image file for a product
   * @param productId Product ID
   * @param file Image file to upload (10MB max, jpeg/png/jpg/gif/webp/svg)
   * @param category Image category
   * @returns Uploaded image
   */
  async uploadImage(
    productId: number,
    file: File,
    category: 'brand_logo' | 'product_image' | 'material'
  ): Promise<ProductImage> {
    try {
      console.log(`Product(ID: ${productId})に画像ファイルをアップロード中...`, file.name);

      const formData = new FormData();
      formData.append('image', file);
      formData.append('category', category);

      const config = {
        timeout: 60000,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      const response = await apiRequest.post<{ data: ProductImage; message: string }>(
        `knowledge/products/${productId}/images`,
        formData,
        config
      );

      console.log('画像ファイルのアップロードが完了しました');
      return response.data;
    } catch (error: any) {
      console.error('画像ファイルのアップロードに失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Delete a product image
   * @param productId Product ID
   * @param imageId Image ID
   * @returns Success message
   */
  async deleteImage(productId: number, imageId: number): Promise<{ message: string }> {
    try {
      console.log(`Product(ID: ${productId})の画像(ID: ${imageId})を削除中...`);

      const config = {
        timeout: 30000,
      };

      const response = await apiRequest.delete<{ message: string }>(
        `knowledge/products/${productId}/images/${imageId}`,
        config
      );

      console.log('画像の削除が完了しました');
      return response;
    } catch (error: any) {
      console.error('画像削除に失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Reorder product images
   * @param productId Product ID
   * @param imageIds Array of image IDs in new order
   * @returns Updated images
   */
  async reorderImages(productId: number, imageIds: number[]): Promise<ProductImagesResponse> {
    try {
      console.log(`Product(ID: ${productId})の画像を並び替え中...`);

      const config = {
        timeout: 30000,
      };

      const response = await apiRequest.post<ProductImagesResponse>(
        `knowledge/products/${productId}/images/reorder`,
        { image_ids: imageIds },
        config
      );

      console.log('画像の並び替えが完了しました');
      return response;
    } catch (error: any) {
      console.error('画像の並び替えに失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Set primary image for a product
   * @param productId Product ID
   * @param imageId Image ID to set as primary
   * @returns Updated image
   */
  async setPrimaryImage(productId: number, imageId: number): Promise<{ data: ProductImage; message: string }> {
    try {
      console.log(`Product(ID: ${productId})のプライマリ画像を設定中...`);

      const config = {
        timeout: 30000,
      };

      const response = await apiRequest.post<{ data: ProductImage; message: string }>(
        `knowledge/products/${productId}/images/${imageId}/primary`,
        {},
        config
      );

      console.log('プライマリ画像の設定が完了しました');
      return response;
    } catch (error: any) {
      console.error('プライマリ画像の設定に失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }
}

export default new ProductImageService();
